import { useState, useEffect } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Calendar, Shield, Award, X, ChevronLeft, ChevronRight } from "lucide-react";

const KennelLitter = () => {
  const { litterId } = useParams();
  const { API } = useOutletContext();
  const [litter, setLitter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/litters/${litterId}`)
      .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(data => { setLitter(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [API, litterId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0D]">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!litter) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0B0D] gap-4">
      <p className="text-[#B8B8B8]/50 text-sm">Nie znaleziono miotu</p>
      <Link to="/mioty" className="text-xs text-[#D4AF37] hover:underline">Wróć do miotów</Link>
    </div>
  );

  const photos = litter.puppy_photos || [];
  const prev = () => setLightbox(i => (i - 1 + photos.length) % photos.length);
  const next = () => setLightbox(i => (i + 1) % photos.length);

  const birthDisplay = litter.birth_date ? new Date(litter.birth_date).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div>
      {/* Header */}
      <section className="bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 pt-16 pb-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.03] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute inset-0 flex items-center justify-end pointer-events-none overflow-hidden">
          <motion.img src="/kennel_motif.png" alt="" className="w-[500px] md:w-[700px] lg:w-[900px] max-w-none h-auto object-contain mr-[-5%]"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.12, 0.12, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", times: [0, 0.2, 0.8, 1] }}
          />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/mioty" className="text-[10px] tracking-[0.15em] text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors mb-4 block" data-testid="back-to-litters">← MIOTY</Link>
            {birthDisplay && <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">{litter.name.toUpperCase()} · {birthDisplay.toUpperCase()}</span>}
            <h1 className="font-serif text-[clamp(2rem,4vw,3.5rem)] text-[#F5F5F5] mb-2" data-testid="litter-title">{litter.name}</h1>
            {litter.puppies_info && <p className="text-[#B8B8B8]/50 text-sm">{litter.puppies_info}</p>}
            <div className="w-16 h-px bg-[#D4AF37]/30 mt-5" />
          </motion.div>
        </div>
      </section>

      {/* Announcement image */}
      {litter.announcement_image && (
        <section className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <img src={litter.announcement_image} alt={`Zapowiedź ${litter.name}`} className="w-full max-w-2xl mx-auto rounded-sm border border-[#2A2A2D]" />
          </motion.div>
        </section>
      )}

      {/* Parents with photos */}
      {(litter.mother?.name || litter.father?.name) && (
        <section className="bg-[#141416] px-6 md:px-12 py-16">
          <div className="max-w-7xl mx-auto">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">RODZICE</span>
            <h2 className="font-serif text-[clamp(1.5rem,3vw,2.2rem)] text-[#F5F5F5] mb-10">Para hodowlana</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[{ label: "MATKA", data: litter.mother }, { label: "OJCIEC", data: litter.father }].map(({ label, data }) => data?.name && (
                <motion.div key={label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-500 group relative"
                >
                  <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#D4AF37]/[0.04] rounded-full blur-[60px] group-hover:bg-[#D4AF37]/[0.10] transition-all duration-700 pointer-events-none" />
                  <div className="grid grid-cols-[160px_1fr] md:grid-cols-[200px_1fr] relative items-start">
                    <div className="overflow-hidden bg-[#141416] w-[160px] md:w-[200px] flex-shrink-0">
                      {data.photo ? (
                        <img src={data.photo} alt={data.name} className="w-full h-auto block group-hover:scale-105 transition-transform duration-700"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#D4AF37]/20 text-5xl font-serif">{data.name[0]}</div>
                      )}
                    </div>
                    <div className="p-5 md:p-6 flex flex-col justify-center">
                      <span className="text-[9px] tracking-[0.2em] text-[#D4AF37]/60 border border-[#D4AF37]/20 px-2 py-0.5 self-start mb-3">{label}</span>
                      <h3 className="font-serif text-lg text-[#F5F5F5] mb-1 group-hover:text-[#D4AF37] transition-colors" data-testid={`litter-${label.toLowerCase()}-name`}>{data.name}</h3>
                      {data.titles && <p className="text-[11px] text-[#D4AF37]/50 tracking-wider mb-1">{data.titles}</p>}
                      {data.born && <p className="text-[11px] text-[#B8B8B8]/40 mb-4">{label === "MATKA" ? "Urodzona" : "Urodzony"}: {data.born}</p>}
                      {data.achievements && (
                        <div className="mb-3">
                          <p className="text-[10px] tracking-[0.15em] text-[#D4AF37]/40 mb-2">OSIĄGNIĘCIA</p>
                          <p className="text-[11px] text-[#B8B8B8]/60">{data.achievements}</p>
                        </div>
                      )}
                      {data.health?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] tracking-[0.15em] text-[#D4AF37]/40 mb-2">BADANIA</p>
                          <div className="space-y-1">
                            {data.health.map((h, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Shield className="w-3 h-3 text-[#D4AF37]/40 flex-shrink-0" />
                                <p className="text-[11px] text-[#B8B8B8]/60">{h}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {data.dog_id && (
                        <Link to={`/psy/${data.dog_id}`} className="text-[10px] tracking-[0.15em] text-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors mt-2 inline-block">
                          ZOBACZ PROFIL →
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Puppy gallery */}
      {photos.length > 0 && (
        <section className="py-16">
          <div className="px-6 md:px-12 max-w-7xl mx-auto mb-6">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">GALERIA</span>
            <h2 className="font-serif text-[clamp(1.5rem,3vw,2.2rem)] text-[#F5F5F5]">Szczenięta z {litter.name}</h2>
          </div>
          <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="litter-puppy-grid">
            {photos.map((photo, idx) => (
              <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: 0.05 + (idx % 8) * 0.03 }}
                className="relative aspect-square overflow-hidden cursor-pointer group bg-[#0B0B0D]"
                onClick={() => setLightbox(idx)} data-testid={`puppy-photo-${idx}`}
              >
                <img src={photo} alt={`Szczenię ${idx + 1}`} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                {(litter.puppy_captions || {})[photo] && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2">
                    <p className="text-[#F5F5F5] text-xs" data-testid={`puppy-caption-${idx}`}>{litter.puppy_captions[photo]}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          </div>
        </section>
      )}

      {/* Info footer */}
      <section className="bg-[#141416] px-6 md:px-12 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <Award className="w-8 h-8 text-[#D4AF37]/30 mx-auto mb-4" />
          <p className="text-[#B8B8B8]/50 text-sm leading-relaxed mb-2">Hodowla na niemieckiej licencji hodowlanej SV Körung</p>
          <p className="text-[#B8B8B8]/30 text-xs">Rezerwacje: +48 663 746 095 | katzenberg.hodowla@gmail.com</p>
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="w-16 h-16 bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl flex items-center justify-center p-2">
              <img src="/logo_zkwp.png" alt="ZKwP" className="w-full h-full object-contain" />
            </div>
            <div className="w-16 h-16 bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl flex items-center justify-center p-2">
              <img src="/logo_fci.png" alt="FCI" className="w-full h-full object-contain" />
            </div>
          </div>
          <Link to="/kontakt" className="inline-block mt-8 px-10 py-3 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300" data-testid="litter-contact-btn">
            SKONTAKTUJ SIĘ Z NAMI
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0B0B0D]/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)}
          >
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#B8B8B8]/50 hover:text-[#D4AF37] transition-colors z-10" data-testid="lightbox-prev"><ChevronLeft className="w-8 h-8" /></button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#B8B8B8]/50 hover:text-[#D4AF37] transition-colors z-10" data-testid="lightbox-next"><ChevronRight className="w-8 h-8" /></button>
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-[#B8B8B8]/50 hover:text-[#F5F5F5] transition-colors z-10" data-testid="lightbox-close"><X className="w-8 h-8" /></button>
            <motion.img key={lightbox} initial={{ opacity: 0 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              src={photos[lightbox]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
            <p className="absolute bottom-6 text-[#B8B8B8]/30 text-[10px] tracking-[0.2em]">{lightbox + 1} / {photos.length}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KennelLitter;
